import { useQuery } from "@tanstack/react-query";
import type { AuditTimelineResponse, TeamMemberProfile } from "@salesboard/contracts";
import { api } from "@/lib/api";

const KEY = "team-member";

/**
 * A colleague's profile.
 *
 * Its own hook rather than a variant of the admin user hooks, because it reads a different
 * endpoint with a different permission and a deliberately narrower shape — everyone may
 * call this, and it carries no email or account state.
 */
export function useTeamMember(slug: string | undefined) {
  return useQuery({
    queryKey: [KEY, slug],
    queryFn: () => api.get<TeamMemberProfile>(`/users/by-slug/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
  });
}

/**
 * The administrative timeline for one user — invitation, credential, role, state.
 *
 * Head-of-Sales-only on the server. `enabled` mirrors that so a Sales Manager's browser
 * never fires a request it would be refused: the gate is the API's, and this only avoids
 * a pointless 403 in the console.
 */
export function useTeamMemberHistory(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, id, "history"],
    queryFn: () => api.get<AuditTimelineResponse>(`/users/${id}/history`),
    enabled: Boolean(id) && enabled,
  });
}
